import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { useToast } from '../hooks/use-toast';
import {
  Workflow, Plus, Play, Pause, Trash2, Settings, CheckCircle, XCircle,
  AlertTriangle, Clock, ArrowRight, Zap, Bell, GraduationCap, FileText,
  Users, RefreshCw, Loader2, ChevronRight, History, Copy, Edit
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const TRIGGER_TYPES = {
  incident_created: { label: 'Incident Created', icon: AlertTriangle, color: 'text-red-500' },
  incident_severity: { label: 'Critical Incident', icon: AlertTriangle, color: 'text-red-600' },
  compliance_deadline: { label: 'Compliance Deadline', icon: Clock, color: 'text-yellow-500' },
  new_job_type: { label: 'New Job Type', icon: FileText, color: 'text-blue-500' },
  training_overdue: { label: 'Training Overdue', icon: GraduationCap, color: 'text-purple-500' },
  sensor_alert: { label: 'Sensor Alert', icon: Zap, color: 'text-orange-500' },
  manual: { label: 'Manual Trigger', icon: Play, color: 'text-gray-500' }
};

const ACTION_TYPES = {
  assign_training: { label: 'Assign Training', icon: GraduationCap },
  escalate_to_manager: { label: 'Escalate to Manager', icon: Users },
  send_reminder: { label: 'Send Reminder', icon: Bell },
  generate_jha: { label: 'Generate JHA', icon: FileText },
  create_notification: { label: 'Create Notification', icon: Bell },
  create_task: { label: 'Create Task', icon: CheckCircle }
};

const WorkflowAutomationPage = () => {
  const { toast } = useToast();
  const [workflows, setWorkflows] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [executions, setExecutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [newWorkflow, setNewWorkflow] = useState({
    name: '',
    description: '',
    trigger_type: 'incident_created',
    conditions: [],
    actions: [{ type: 'create_notification', config: { title: '', message: '', severity: 'info' } }]
  });

  const fetchWorkflows = useCallback(async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const [workflowsRes, templatesRes, executionsRes] = await Promise.all([
        axios.get(`${API}/api/workflows`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/workflows/templates`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/workflows/executions?hours=168`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setWorkflows(workflowsRes.data.workflows || []);
      setTemplates(templatesRes.data.templates || []);
      setExecutions(executionsRes.data.executions || []);
    } catch (error) {
      console.error('Failed to fetch workflows:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWorkflows();
  }, [fetchWorkflows]);

  const toggleWorkflow = async (workflowId) => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.post(`${API}/api/workflows/${workflowId}/toggle`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({
        title: response.data.enabled ? 'Workflow Enabled' : 'Workflow Disabled'
      });
      fetchWorkflows();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to toggle workflow', variant: 'destructive' });
    }
  };

  const deleteWorkflow = async (workflowId) => {
    if (!window.confirm('Are you sure you want to delete this workflow?')) return;
    
    try {
      const token = localStorage.getItem('auth_token');
      await axios.delete(`${API}/api/workflows/${workflowId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: 'Workflow Deleted' });
      fetchWorkflows();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete workflow', variant: 'destructive' });
    }
  };

  const applyTemplate = async (templateId) => {
    try {
      const token = localStorage.getItem('auth_token');
      await axios.post(`${API}/api/workflows/templates/${templateId}/apply`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: 'Template Applied', description: 'New workflow created from template' });
      fetchWorkflows();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to apply template', variant: 'destructive' });
    }
  };

  const createWorkflow = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      await axios.post(`${API}/api/workflows`, newWorkflow, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: 'Workflow Created' });
      setShowCreateDialog(false);
      setNewWorkflow({
        name: '',
        description: '',
        trigger_type: 'incident_created',
        conditions: [],
        actions: [{ type: 'create_notification', config: { title: '', message: '', severity: 'info' } }]
      });
      fetchWorkflows();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to create workflow',
        variant: 'destructive'
      });
    }
  };

  const testWorkflow = async (workflowId) => {
    try {
      const token = localStorage.getItem('auth_token');
      const testData = {
        id: 'test-001',
        title: 'Test Incident',
        severity: 'critical',
        department: 'Construction',
        location: 'Site A'
      };
      
      const response = await axios.post(`${API}/api/workflows/trigger/test/${workflowId}`, testData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: 'Test Complete',
        description: `Status: ${response.data.execution?.status}. Actions: ${response.data.execution?.actions_executed?.length || 0}`
      });
      fetchWorkflows();
    } catch (error) {
      toast({ title: 'Error', description: 'Test failed', variant: 'destructive' });
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="workflow-automation-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Workflow Automation</h1>
          <p className="text-gray-500">Automate safety tasks and notifications</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={fetchWorkflows}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700">
                <Plus className="w-4 h-4 mr-2" />
                Create Workflow
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Create New Workflow</DialogTitle>
                <DialogDescription>Define automation rules for safety tasks</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Workflow Name *</Label>
                  <Input
                    placeholder="e.g., Critical Incident Response"
                    value={newWorkflow.name}
                    onChange={(e) => setNewWorkflow({ ...newWorkflow, name: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea
                    placeholder="What does this workflow do?"
                    value={newWorkflow.description}
                    onChange={(e) => setNewWorkflow({ ...newWorkflow, description: e.target.value })}
                    rows={2}
                  />
                </div>
                <div>
                  <Label>Trigger Type *</Label>
                  <Select
                    value={newWorkflow.trigger_type}
                    onValueChange={(v) => setNewWorkflow({ ...newWorkflow, trigger_type: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(TRIGGER_TYPES).map(([key, { label }]) => (
                        <SelectItem key={key} value={key}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Action Type *</Label>
                  <Select
                    value={newWorkflow.actions[0]?.type}
                    onValueChange={(v) => setNewWorkflow({
                      ...newWorkflow,
                      actions: [{ ...newWorkflow.actions[0], type: v }]
                    })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(ACTION_TYPES).map(([key, { label }]) => (
                        <SelectItem key={key} value={key}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={createWorkflow} className="w-full">Create Workflow</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Workflow className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{workflows.length}</p>
                <p className="text-sm text-gray-500">Total Workflows</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-green-100 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{workflows.filter(w => w.enabled).length}</p>
                <p className="text-sm text-gray-500">Active</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-purple-100 rounded-lg">
                <History className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{executions.length}</p>
                <p className="text-sm text-gray-500">Executions (7d)</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-orange-100 rounded-lg">
                <Zap className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{templates.length}</p>
                <p className="text-sm text-gray-500">Templates</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="workflows">
        <TabsList>
          <TabsTrigger value="workflows">Active Workflows</TabsTrigger>
          <TabsTrigger value="templates">Templates</TabsTrigger>
          <TabsTrigger value="history">Execution History</TabsTrigger>
        </TabsList>

        {/* Active Workflows Tab */}
        <TabsContent value="workflows" className="mt-4">
          <div className="space-y-4">
            {workflows.length === 0 ? (
              <Card className="py-12">
                <CardContent className="text-center">
                  <Workflow className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900">No workflows yet</h3>
                  <p className="text-gray-500 mb-4">Create a workflow or apply a template to get started</p>
                  <Button onClick={() => setShowCreateDialog(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Create Workflow
                  </Button>
                </CardContent>
              </Card>
            ) : (
              workflows.map((workflow) => {
                const trigger = TRIGGER_TYPES[workflow.trigger_type] || TRIGGER_TYPES.manual;
                const TriggerIcon = trigger.icon;
                
                return (
                  <Card key={workflow.id} className={!workflow.enabled ? 'opacity-60' : ''}>
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4">
                          <div className={`p-3 rounded-lg bg-gray-100`}>
                            <TriggerIcon className={`w-5 h-5 ${trigger.color}`} />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="font-medium">{workflow.name}</h3>
                              <Badge variant={workflow.enabled ? 'default' : 'secondary'}>
                                {workflow.enabled ? 'Active' : 'Disabled'}
                              </Badge>
                            </div>
                            <p className="text-sm text-gray-500 mt-1">{workflow.description || 'No description'}</p>
                            <div className="flex items-center gap-4 mt-3">
                              <div className="flex items-center gap-2 text-sm text-gray-600">
                                <span className="font-medium">Trigger:</span>
                                <span>{trigger.label}</span>
                              </div>
                              <div className="flex items-center gap-2 text-sm text-gray-600">
                                <span className="font-medium">Actions:</span>
                                <span>{workflow.actions?.length || 0}</span>
                              </div>
                              <div className="flex items-center gap-2 text-sm text-gray-600">
                                <span className="font-medium">Runs:</span>
                                <span>{workflow.execution_count || 0}</span>
                              </div>
                            </div>
                            {workflow.last_executed_at && (
                              <p className="text-xs text-gray-400 mt-2">
                                Last run: {formatDate(workflow.last_executed_at)}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => testWorkflow(workflow.id)}
                          >
                            <Play className="w-4 h-4 mr-1" />
                            Test
                          </Button>
                          <Switch
                            checked={workflow.enabled}
                            onCheckedChange={() => toggleWorkflow(workflow.id)}
                          />
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-red-500 hover:text-red-700"
                            onClick={() => deleteWorkflow(workflow.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>
        </TabsContent>

        {/* Templates Tab */}
        <TabsContent value="templates" className="mt-4">
          <div className="grid grid-cols-2 gap-4">
            {templates.map((template) => {
              const trigger = TRIGGER_TYPES[template.trigger_type] || TRIGGER_TYPES.manual;
              const TriggerIcon = trigger.icon;
              
              return (
                <Card key={template.id} className="hover:shadow-md transition-shadow">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-100 rounded-lg">
                          <TriggerIcon className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <CardTitle className="text-lg">{template.name}</CardTitle>
                          <CardDescription>{template.description}</CardDescription>
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 text-sm">
                        <Badge variant="outline">{trigger.label}</Badge>
                        <ArrowRight className="w-4 h-4 text-gray-400" />
                        {template.actions?.map((action, i) => (
                          <Badge key={i} variant="secondary">
                            {ACTION_TYPES[action.type]?.label || action.type}
                          </Badge>
                        ))}
                      </div>
                      <Button
                        variant="outline"
                        className="w-full"
                        onClick={() => applyTemplate(template.id)}
                      >
                        <Copy className="w-4 h-4 mr-2" />
                        Apply Template
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* Execution History Tab */}
        <TabsContent value="history" className="mt-4">
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Workflow</TableHead>
                  <TableHead>Trigger</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                  <TableHead>Executed At</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {executions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                      No executions in the last 7 days
                    </TableCell>
                  </TableRow>
                ) : (
                  executions.slice(0, 20).map((exec) => (
                    <TableRow key={exec.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{exec.workflow_name}</p>
                          <p className="text-xs text-gray-500">{exec.workflow_id?.slice(0, 8)}...</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {TRIGGER_TYPES[exec.trigger_type]?.label || exec.trigger_type}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={exec.status === 'completed' ? 'default' : exec.status === 'failed' ? 'destructive' : 'secondary'}
                        >
                          {exec.status}
                        </Badge>
                      </TableCell>
                      <TableCell>{exec.actions_executed?.length || 0}</TableCell>
                      <TableCell className="text-gray-500">{formatDate(exec.started_at)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default WorkflowAutomationPage;
